// Centralized user-facing notification system.
//
// Two integration paths so callers always pick the right one:
//
// 1. useAppNotifications() — React hook used inside components. It
//    calls AntApp.useApp() internally, so the returned popup methods
//    are guaranteed to be theme/context-aware. Prefer this in pages
//    and modals.
//
// 2. registerNotifier(inst) — called once from <NotifyBridge> mounted
//    inside <AntApp>. Captures the result of AntApp.useApp() at module
//    scope so non-React callers (axios interceptors, plain utilities,
//    background tasks) can fire popups via the exported notify*/show*
//    functions without re-entering the React tree.
//
// Public API (both paths expose the same surface):
//   notifySuccess(title, description, options)
//   notifyInfo(title, description, options)
//   notifyWarning(title, description, options)
//   notifyError(error, fallback?, options)
//   showErrorModal(error, fallback?, options)
//   showWarningModal(title, description, options)
//   showSuccessModal(title, description, options)
//   flashMessage(text, kind)
//
// Plus parseApiError(error) which is exported for callers that want
// the friendly { code, title, description, severity, requestId }
// without firing a popup.
//
// Safety invariants — enforced everywhere:
//   - We never expose stack traces, raw axios objects, request configs,
//     SQL fragments, tokens, OTP values, passwords, or Razorpay
//     signatures.
//   - We never echo the backend `code` as the user-facing title; codes
//     are converted to friendly copy via CODE_MAP, falling through to
//     a generic safe pair.
//   - We never use the words "tenant" / "schema" / "transaction id" in
//     user-facing copy.
//   - When the bridge has not yet registered, calls are quietly
//     dropped except for a dev-only console.warn.

import { useMemo } from 'react';
import { App as AntApp } from 'antd';
import {
  supportConfig,
  supportMailtoHref,
  supportTelHref
} from '../config/support';

// ---------------- module-level instance (set by <NotifyBridge>) ----

let _notification = null;
let _modal = null;
let _message = null;

export function registerNotifier({ notification, modal, message } = {}) {
  _notification = notification || null;
  _modal = modal || null;
  _message = message || null;
}

// ---------------- friendly mapping ---------------------------------

// Severity drives the *default* surface used by notifyError when a
// caller doesn't override it. Important API/business errors block
// with a modal; benign / non-actionable messages stay as toasts:
//   - 'error'         -> modal.error          (default for failures)
//   - 'modal'         -> modal.error          (alias of 'error')
//   - 'warning'       -> modal.warning        (default for soft failures)
//   - 'modal-warning' -> modal.warning        (alias of 'warning')
//   - 'info'          -> notification.info    (toast)
//   - 'toast'         -> notification.error   (opt-out for callers that
//                                              really want a toast)
//   - 'toast-warning' -> notification.warning
//
// Spec: invalid OTP / login failed / network failure / payment failed
// must NOT be a fly-by toast — they need an OK button.
//
// `appendSupport: true` causes the support-contact line to be added
// to the rendered description. Reserve it for cases where the user
// genuinely needs an out-of-band channel (payment verification gone
// wrong, blocked signup, repeated server failures).
const CODE_MAP = {
  // ---- auth ----
  AUTH_INVALID_CREDENTIALS: {
    title: 'Login failed',
    description:
      'The email or password you entered is incorrect. Please check your login details and try again.',
    severity: 'error'
  },
  INVALID_CREDENTIALS: {
    title: 'Login failed',
    description:
      'The email or password you entered is incorrect. Please check your login details and try again.',
    severity: 'error'
  },
  INVALID_EMAIL_DOMAIN: {
    title: 'Email format not recognised',
    description:
      'Use your @master.glass-i or @user.glass-i email to sign in.',
    severity: 'warning'
  },
  UNAUTHORIZED: {
    title: 'Session expired',
    description: 'Please log in again to continue.',
    severity: 'warning'
  },
  SESSION_EXPIRED: {
    title: 'Session expired',
    description: 'Please log in again to continue.',
    severity: 'warning'
  },
  FORBIDDEN: {
    title: 'Access denied',
    description: 'You do not have permission to perform this action.',
    severity: 'warning'
  },

  // ---- signup ----
  SIGNUP_IN_PROGRESS: {
    // The SignupStartPage handles SIGNUP_IN_PROGRESS with a custom
    // modal that uses backend `details` (expiry, currentStep, etc.).
    // This entry is the safety-net wording in case any other caller
    // hits the code via the generic path.
    title: 'Signup already in progress',
    description:
      'A signup is already active for this GSTIN or email. Please continue the existing signup or wait until it expires.',
    severity: 'warning'
  },
  // Stage 5N-13.1 UX — keep these CODE_MAP entries in sync with
  // gstinErrorMessages.js. The signup START page routes GSTIN-bucket
  // errors through that helper directly so it can disambiguate
  // GSTIN_NOT_ACTIVE via details.gstinStatus and refocus the GSTIN
  // input on close. This map is the safety net for any OTHER caller
  // that hits these codes via the generic notifyError pipeline (e.g.
  // an admin-side flow). Every entry now uses the same professional
  // copy as the helper templates so the user never sees the awkward
  // "your GSTIN was not rejected" wording the previous version had.
  INVALID_GSTIN: {
    title: 'Invalid GSTIN format',
    description:
      'The GSTIN entered does not appear to be valid. Please check the 15-character GSTIN and try again.',
    severity: 'warning'
  },
  GSTIN_INVALID: {
    title: 'Invalid GSTIN format',
    description:
      'The GSTIN entered does not appear to be valid. Please check the 15-character GSTIN and try again.',
    severity: 'warning'
  },
  GSTIN_FORMAT_INVALID: {
    title: 'Invalid GSTIN format',
    description:
      'The GSTIN entered does not appear to be valid. Please check the 15-character GSTIN and try again.',
    severity: 'warning'
  },
  GSTIN_NOT_FOUND: {
    title: 'GSTIN not found',
    description:
      'We could not find this GSTIN in the GST records. Please verify the GSTIN and enter it exactly as shown on your GST certificate.',
    severity: 'error'
  },
  GSTIN_INACTIVE: {
    title: 'GSTIN is not active',
    description:
      'This GSTIN appears to be inactive, cancelled, or suspended. Please use an active GSTIN to continue.',
    severity: 'error'
  },
  GSTIN_PROVIDER_TIMEOUT: {
    title: 'Verification timed out',
    description:
      'The GSTIN verification request took too long to complete. Please check your connection and try again.',
    severity: 'warning'
  },
  GSTIN_VERIFICATION_TIMEOUT: {
    title: 'Verification timed out',
    description:
      'The GSTIN verification request took too long to complete. Please check your connection and try again.',
    severity: 'warning'
  },
  GSTIN_PROVIDER_UNAVAILABLE: {
    title: 'GST verification temporarily unavailable',
    description:
      'We are unable to verify GSTIN details right now because the GST verification service is not responding. Please try again in a few minutes.',
    severity: 'warning'
  },
  GSTIN_PROVIDER_AUTH_FAILED: {
    title: 'GST verification temporarily unavailable',
    description:
      'GSTIN verification is currently unavailable. Our team has been notified — please try again in a few minutes.',
    severity: 'warning'
  },
  GSTIN_PROVIDER_ERROR: {
    title: 'GST verification temporarily unavailable',
    description:
      'We are unable to verify GSTIN details right now because the GST verification service is not responding. Please try again in a few minutes.',
    severity: 'warning'
  },
  GSTIN_LEGAL_NAME_MISMATCH: {
    title: 'Company details need review',
    description:
      'The GSTIN was found, but the registered legal name does not appear to match the company details entered. Please review the details before continuing.',
    severity: 'warning'
  },
  GSTIN_VERIFICATION_FAILED: {
    title: 'Unable to verify GSTIN',
    description:
      'We could not complete GSTIN verification right now. Please try again. If the issue continues, contact support.',
    severity: 'error'
  },
  // Stage 5N-12: strict ACT-only rule. Provider returned a non-active
  // status (cancelled/suspended/inactive/provisional/anything other
  // than ACT/Active). The signup START page handles this via
  // gstinErrorMessages.js so it can render "GSTIN not found" vs
  // "GSTIN is not active" depending on details.gstinStatus. This
  // entry is the safety net for any other caller and uses the
  // safer-default INACTIVE copy.
  GSTIN_NOT_ACTIVE: {
    title: 'GSTIN is not active',
    description:
      'This GSTIN appears to be inactive, cancelled, or suspended. Please use an active GSTIN to continue.',
    severity: 'error'
  },

  // ---- Stage 5N-10: SEZ tax-treatment ----
  SEZ_DECLARATION_REQUIRED: {
    title: 'SEZ declaration required',
    description:
      'Please confirm the authorised-operations declaration before continuing as an SEZ Unit / Developer.',
    severity: 'warning'
  },
  SEZ_APPROVAL_NUMBER_REQUIRED: {
    title: 'SEZ approval number required',
    description:
      'Enter the SEZ approval / LOA number issued to your unit or developer.',
    severity: 'warning'
  },
  SEZ_NOT_DECLARED: {
    title: 'SEZ status not declared',
    description:
      'This account did not declare SEZ status during signup. SEZ approval applies only to SEZ-declared customers.',
    severity: 'warning'
  },
  SEZ_DECLARATION_MISSING: {
    title: 'SEZ declaration missing',
    description:
      'The authorised-operations declaration is missing on this tenant. Ask the customer to re-confirm before approving.',
    severity: 'warning'
  },
  SEZ_TRANSITION_INVALID: {
    title: 'SEZ status transition not allowed',
    description: 'This SEZ status change is not allowed from the current state.',
    severity: 'warning'
  },
  // Stage 5N-12: SEZ payment-gating codes shown to the customer when
  // their SEZ registration is awaiting / has been refused by an admin.
  // The customer cannot proceed to plan/payment until the admin
  // approves and commits a tax decision.
  SEZ_APPROVAL_PENDING: {
    title: 'SEZ approval pending',
    description:
      'Your SEZ registration is pending internal approval. You can proceed to payment after approval. We will notify you by email.',
    severity: 'info'
  },
  SEZ_APPROVAL_REJECTED: {
    title: 'SEZ registration rejected',
    description:
      'Your SEZ registration was rejected. Please contact support to review your declaration.',
    severity: 'error'
  },
  SEZ_TAX_DECISION_INVALID: {
    title: 'SEZ tax decision invalid',
    description:
      'Tax treatment and tax percent must agree (sez_zero_rated must be 0%; regular_taxable must be > 0%).',
    severity: 'warning'
  },

  // ---- otp ----
  OTP_INVALID: {
    title: 'Invalid OTP',
    description:
      'The OTP you entered is incorrect. Please check and try again.',
    severity: 'error'
  },
  OTP_EXPIRED: {
    title: 'OTP expired',
    description: 'Please request a new OTP and try again.',
    severity: 'warning'
  },
  OTP_RATE_LIMITED: {
    title: 'Too many OTP attempts',
    description: 'Please wait a few minutes before trying again.',
    severity: 'warning'
  },

  // ---- account / sub-users ----
  USERNAME_TAKEN: {
    title: 'Username unavailable',
    description: 'Please choose a different master username.',
    severity: 'error'
  },

  // ---- payment ----
  PAYMENT_FAILED: {
    title: 'Payment failed',
    description:
      'Your payment was not completed. Please try again with a fresh payment attempt.',
    severity: 'error'
  },
  PAYMENT_VERIFICATION_FAILED: {
    title: 'Payment verification failed',
    description:
      'If your amount was deducted, please contact support. Do not pay again immediately.',
    severity: 'modal',
    appendSupport: true
  },
  PAYMENT_CANCELLED: {
    title: 'Payment was cancelled',
    description: 'You can try again when ready. No charge was made.',
    severity: 'info'
  },
  PAYMENT_RECOVERY_NOT_FOUND: {
    title: 'No completed payment found',
    description:
      'No captured payment was found for this attempt. Please start a fresh payment.',
    severity: 'warning'
  },

  // ---- Stage 5K: trial / coupon ----
  TRIAL_ALREADY_USED: {
    title: 'Trial already used',
    description:
      'Trial already used for this GSTIN. Please choose the Standard Plan.',
    severity: 'warning'
  },

  // ---- Stage 5K-CustomPlan: Custom Plan / Custom Code ----
  // Backend retains plan_coupons / COUPON_* error codes internally for
  // historical reasons; user-facing copy uses "Custom code" / "Custom
  // plan" wording exclusively. Severity stays 'warning' so each one
  // surfaces as a blocking modal (the user must clear it before they
  // can retry the input field).
  CUSTOM_CODE_INVALID: {
    title: 'Invalid custom code',
    description: 'The custom code is invalid or expired.',
    severity: 'warning'
  },
  COUPON_NOT_FOUND: {
    title: 'Invalid custom code',
    description: 'The custom code is invalid or expired.',
    severity: 'warning'
  },
  COUPON_INVALID: {
    title: 'Invalid custom code',
    description: 'The custom code is invalid or expired.',
    severity: 'warning'
  },
  COUPON_INVALID_FORMAT: {
    title: 'Invalid custom code',
    description:
      'Custom code must be 3–40 characters using letters, digits, dash, or underscore.',
    severity: 'warning'
  },
  COUPON_EXPIRED: {
    title: 'Custom code expired',
    description: 'This custom code has expired.',
    severity: 'warning'
  },
  COUPON_NOT_YET_ACTIVE: {
    title: 'Custom code unavailable',
    description: 'This custom code is not yet active.',
    severity: 'warning'
  },
  COUPON_INACTIVE: {
    title: 'Custom code unavailable',
    description: 'This custom code is not currently available.',
    severity: 'warning'
  },
  COUPON_MAX_REDEMPTIONS_REACHED: {
    title: 'Custom code limit reached',
    description: 'This custom code has already been used.',
    severity: 'warning'
  },
  COUPON_REDEMPTION_LIMIT_REACHED: {
    title: 'Custom code limit reached',
    description: 'This custom code has already been used.',
    severity: 'warning'
  },
  COUPON_ALREADY_USED: {
    title: 'Custom code already used',
    description: 'This custom code has already been used for this GSTIN.',
    severity: 'warning'
  },
  COUPON_NOT_ELIGIBLE: {
    title: 'Custom code not available',
    description: 'This custom code is not available for this account.',
    severity: 'warning'
  },
  COUPON_NOT_ALLOWED_FOR_GSTIN: {
    title: 'Custom code not available',
    description: 'This custom code is not available for this GSTIN.',
    severity: 'warning'
  },
  COUPON_NOT_ALLOWED_FOR_EMAIL: {
    title: 'Custom code not available',
    description: 'This custom code is not available for this email.',
    severity: 'warning'
  },
  COUPON_PLAN_NOT_AVAILABLE: {
    title: 'Custom plan unavailable',
    description:
      'The custom plan linked to this code is not currently available.',
    severity: 'warning'
  },
  COUPON_PLAN_MISSING: {
    title: 'Custom plan unavailable',
    description:
      'The custom plan linked to this code is not currently available.',
    severity: 'error',
    appendSupport: true
  },
  // 5K-CustomPlan: kept for any in-flight flow that still surfaces it,
  // but the new public path NEVER returns requires_payment=false — the
  // backend rejects that with CUSTOM_PLAN_PAYMENT_REQUIRED below.
  COUPON_REQUIRES_PAYMENT: {
    title: 'Payment required',
    description:
      'This custom plan requires payment. Please continue to the payment step.',
    severity: 'warning'
  },
  CUSTOM_PLAN_PAYMENT_REQUIRED: {
    title: 'Custom plan unavailable',
    description:
      'This custom plan is not available for public signup. Please contact support.',
    severity: 'warning',
    appendSupport: true
  },
  CUSTOM_PLAN_INVALID_CONFIG: {
    title: 'Custom plan unavailable',
    description:
      'We could not load this custom plan. Please contact support.',
    severity: 'error',
    appendSupport: true
  },
  CUSTOM_PLAN_VERIFY_FAILED: {
    title: 'Payment could not be verified',
    description:
      'We received the payment response but could not complete custom plan activation. If the amount was deducted, contact support with your payment reference.',
    severity: 'modal',
    appendSupport: true
  },
  PUBLIC_NO_PAYMENT_DISABLED: {
    title: 'Plan unavailable',
    description:
      'This plan is not available for public signup. Please choose a paid plan.',
    severity: 'warning'
  },

  // ---- Stage 5N-3: internal-admin Custom Plan management ----
  // These codes surface in the internal-admin SPA (/internal/custom-plans).
  // Wording is operator-facing — not what an end-user redeeming a code
  // sees (those go through the COUPON_* and CUSTOM_PLAN_PAYMENT_REQUIRED
  // mappings above).
  CUSTOM_PLAN_CODE_EXISTS: {
    title: 'Custom code already exists',
    description: 'Use a different Custom Code — codes must be unique.',
    severity: 'warning'
  },
  CUSTOM_PLAN_NOT_FOUND: {
    title: 'Custom Plan not found',
    description: 'This Custom Plan may have been removed or never existed.',
    severity: 'warning'
  },
  CUSTOM_PLAN_INVALID: {
    title: 'Custom Plan is invalid',
    description: 'One of the Custom Plan fields is invalid. Check the form and try again.',
    severity: 'warning'
  },
  CUSTOM_PLAN_INACTIVE: {
    title: 'Custom Plan inactive',
    description: 'This Custom Plan is currently deactivated and cannot be redeemed.',
    severity: 'warning'
  },
  CUSTOM_PLAN_EXPIRED: {
    title: 'Custom Plan expired',
    description: 'This Custom Plan has reached its expiry date.',
    severity: 'warning'
  },
  CUSTOM_PLAN_REDEMPTION_LIMIT_REACHED: {
    title: 'Redemption limit reached',
    description: 'This Custom Plan has been fully redeemed and cannot accept more signups.',
    severity: 'warning'
  },
  CUSTOM_PLAN_GSTIN_NOT_ALLOWED: {
    title: 'GSTIN not allowed',
    description: 'This Custom Plan is restricted to a different GSTIN.',
    severity: 'warning'
  },
  CUSTOM_PLAN_EMAIL_NOT_ALLOWED: {
    title: 'Email not allowed',
    description: 'This Custom Plan is restricted to a different customer email.',
    severity: 'warning'
  },
  CUSTOM_PLAN_HAS_REDEMPTIONS: {
    title: 'Custom Plan has redemptions',
    description:
      'This Custom Plan has been redeemed by customers. Some fields can no longer be changed.',
    severity: 'warning'
  },
  CUSTOM_PLAN_TRANSITION_INVALID: {
    title: 'Action not allowed',
    description: 'This Custom Plan is already in the requested state.',
    severity: 'warning'
  },

  // ---- Stage 5N-4: Pricing & Tax master ----
  GST_RATE_NOT_FOUND: {
    title: 'GST rate not found',
    description: 'The GST rate you selected no longer exists.',
    severity: 'warning'
  },
  GST_RATE_INACTIVE: {
    title: 'GST rate inactive',
    description: 'That GST rate has been deactivated. Pick a different rate.',
    severity: 'warning'
  },
  GST_RATE_DEFAULT_LOCKED: {
    title: 'Cannot deactivate the default GST rate',
    description: 'Set a different rate as default before deactivating this one.',
    severity: 'warning'
  },
  GST_RATE_MISSING: {
    title: 'No GST rate configured',
    description: 'Add an active GST rate before saving pricing.',
    severity: 'warning'
  },
  PRICING_CONFIG_MISSING: {
    title: 'Pricing not configured',
    description: 'No active Standard Plan pricing row found. Contact a super admin.',
    severity: 'error',
    appendSupport: true
  },

  // ---- Stage 5K-CustomPlan: account approval gate ----
  ACCOUNT_PENDING_APPROVAL: {
    title: 'Account pending approval',
    description:
      'Your account is pending approval. We will notify you once it is activated.',
    severity: 'warning'
  },
  ACCOUNT_REJECTED: {
    title: 'Account not approved',
    description:
      'Your account approval was not completed. Please contact support.',
    severity: 'warning',
    appendSupport: true
  },
  ACCOUNT_SUSPENDED: {
    title: 'Account suspended',
    description: 'Your account is suspended. Please contact support.',
    severity: 'warning',
    appendSupport: true
  },
  ACCOUNT_INACTIVE: {
    title: 'Account inactive',
    description: 'Your account is not currently active. Please contact support.',
    severity: 'warning',
    appendSupport: true
  },
  TRIAL_SEAT_UPGRADE_REQUIRED: {
    title: 'Upgrade required',
    description:
      'The Trial Plan includes 5 users. Please upgrade to the Standard Plan to add more users.',
    severity: 'warning'
  },

  // ---- subscription / seats ----
  // Friendly mappings for the add-seats flow on /subscription. Without
  // these, every backend rejection (validation, expired period, hard
  // ceiling, or upstream Razorpay failure) used to surface as the
  // generic "Could not fetch quote / Could not create order" toast.
  NO_ACTIVE_SUBSCRIPTION: {
    title: 'No active subscription',
    description:
      'Please choose a plan before adding users.',
    severity: 'warning'
  },
  INVALID_SEAT_COUNT: {
    title: 'Invalid user count',
    description:
      'Enter a valid number of additional users.',
    severity: 'warning'
  },
  SEAT_QUOTE_REQUIRED: {
    title: 'Quote required',
    description:
      'Please get a fresh quote before creating the order.',
    severity: 'warning'
  },
  SEAT_QUOTE_EXPIRED: {
    title: 'Quote expired',
    description:
      'Please get a fresh quote before creating the order.',
    severity: 'warning'
  },
  SEAT_LIMIT_EXCEEDED: {
    title: 'User limit reached',
    description:
      'Adding more users would exceed the maximum allowed on your account. Please contact support.',
    severity: 'warning',
    appendSupport: true
  },
  PERIOD_EXPIRED: {
    title: 'Subscription period ended',
    description:
      'Your current subscription period has ended. Please renew before adding more users.',
    severity: 'warning'
  },
  SUBSCRIPTION_SUSPENDED: {
    title: 'Subscription suspended',
    description:
      'Your subscription is suspended. Please renew before adding more users.',
    severity: 'warning'
  },
  SUBSCRIPTION_PAST_DUE: {
    title: 'Renewal required',
    description:
      'Your subscription is past due. Please renew before adding more users.',
    severity: 'warning'
  },
  USER_SLOT_LIMIT_REACHED: {
    title: 'No user slots available',
    description:
      'Your current plan has no available user slots. Add users from Subscription or surrender an unused user before creating a new login.',
    severity: 'warning'
  },
  // Stage 5K-5A: lifecycle-state codes returned by login + lifecycle endpoints.
  USER_LOCKED: {
    title: 'Account locked',
    description:
      'This user account is locked. Please contact your account administrator.',
    severity: 'warning'
  },
  USER_SURRENDERED: {
    title: 'Account inactive',
    description:
      'This user account is no longer active. Please contact your account administrator.',
    severity: 'warning'
  },
  USER_ACTION_NOT_ALLOWED: {
    title: 'Action not allowed',
    description:
      'This action is not allowed for the selected user.',
    severity: 'warning'
  },
  CANNOT_MODIFY_MASTER: {
    title: 'Action not allowed',
    description:
      'The master account cannot be locked or surrendered.',
    severity: 'warning'
  },

  // Stage 5M: Master-controlled MFA codes. The login MFA codes
  // surface during /login, /api/auth/login/mfa/verify; the setup
  // codes surface in MasterMfaSection / UserMfaSection.
  MFA_REQUIRED: {
    title: 'MFA required',
    description:
      'Enter the 6-digit authenticator code to continue.',
    severity: 'warning'
  },
  MFA_INVALID_CODE: {
    title: 'Invalid MFA code',
    description:
      'The MFA code is incorrect or expired. Please try again.',
    severity: 'error'
  },
  MFA_TOO_MANY_ATTEMPTS: {
    title: 'Too many MFA attempts',
    description:
      'Please wait a few minutes and try again.',
    severity: 'warning'
  },
  MFA_CHALLENGE_EXPIRED: {
    title: 'MFA session expired',
    description: 'Please log in again.',
    severity: 'warning'
  },
  MFA_NOT_CONFIGURED: {
    title: 'MFA not configured',
    description:
      'MFA setup is required before this action can be completed. Contact your administrator.',
    severity: 'warning'
  },
  MFA_QR_GENERATION_FAILED: {
    title: 'Could not generate QR',
    description:
      'We could not generate the MFA QR code. Please try again.',
    severity: 'error'
  },
  // Stage 5M-fix: the setup window elapsed between scanning the QR and
  // entering the code, so the staged secret is gone. We hand the master
  // a fresh QR automatically — this message just explains why.
  MFA_SETUP_EXPIRED: {
    title: 'Setup session expired',
    description:
      'Too much time passed after the QR was shown. We generated a fresh QR — scan it again and enter the new code.',
    severity: 'warning'
  },

  // Stage 5L: per-user login IP access codes.
  // IP_NOT_ALLOWED is intentionally a modal — a blocked login is a hard
  // stop the user must acknowledge, not a fly-by toast.
  IP_NOT_ALLOWED: {
    title: 'Access not allowed',
    description:
      'You are not allowed to access this account from this IP address.',
    severity: 'error',
    appendSupport: true
  },
  CURRENT_IP_NOT_WHITELISTED: {
    title: 'Current IP not added',
    description:
      'Add your current IP address before enabling whitelist-only access for your own account.',
    severity: 'warning'
  },
  INVALID_IP_ADDRESS: {
    title: 'Invalid IP address',
    description: 'Enter a valid IPv4 or IPv6 address.',
    severity: 'warning'
  },
  IP_LIMIT_EXCEEDED: {
    title: 'Too many IP addresses',
    description:
      'You can add up to 20 whitelisted IP addresses for one user.',
    severity: 'warning'
  },
  IP_WHITELIST_EMPTY: {
    title: 'Whitelist is empty',
    description:
      'Add at least one IP address before enabling whitelist-only access.',
    severity: 'warning'
  },

  // ---- Stage 5K-5B: profile + password ----
  USER_NOT_FOUND: {
    title: 'User not found',
    description:
      'The user you tried to act on no longer exists. Please refresh the list.',
    severity: 'warning'
  },
  PROFILE_UPDATE_FAILED: {
    title: 'Could not save profile',
    description:
      'We could not save the profile. Please check the fields and try again.',
    severity: 'error'
  },
  PASSWORD_RESET_FAILED: {
    title: 'Could not reset password',
    description:
      'We could not reset the password. Please try again.',
    severity: 'error'
  },
  PASSWORD_CHANGE_FAILED: {
    title: 'Could not change password',
    description:
      'We could not change your password. Please try again.',
    severity: 'error'
  },
  CURRENT_PASSWORD_INVALID: {
    title: 'Current password is incorrect',
    description:
      'The current password you entered does not match. Please try again.',
    severity: 'warning'
  },
  PASSWORD_REUSE_NOT_ALLOWED: {
    title: 'Choose a different password',
    description:
      'Your new password must be different from your current password.',
    severity: 'warning'
  },
  PASSWORD_POLICY_VIOLATION: {
    title: 'Password is too weak',
    description:
      'Use at least 12 characters with upper, lower, digit, and a symbol.',
    severity: 'warning'
  },
  // ---- Stage 5N-6: Forgot Password ----
  RESET_TOKEN_INVALID: {
    title: 'Reset link expired',
    description:
      'This password reset link is invalid or expired. Please request a new one.',
    severity: 'error'
  },
  RESET_TOKEN_EXPIRED: {
    title: 'Reset link expired',
    description:
      'This password reset link is invalid or expired. Please request a new one.',
    severity: 'error'
  },
  RESET_TOKEN_USED: {
    title: 'Reset link already used',
    description:
      'This password reset link has already been used. Please request a new one if you still need to reset your password.',
    severity: 'error'
  },
  PASSWORD_CONFIRM_MISMATCH: {
    title: 'Passwords do not match',
    description: 'Enter the same password in both fields.',
    severity: 'warning'
  },
  PASSWORD_RESET_RATE_LIMITED: {
    title: 'Too many reset requests',
    description: 'Please wait a few minutes before trying again.',
    severity: 'warning'
  },
  // ---- Stage 5N-7: subscription lifecycle ----
  SUBSCRIPTION_EXPIRED: {
    title: 'Subscription expired',
    description:
      'Your subscription has expired. Please renew your plan to continue.',
    severity: 'error'
  },
  SUBSCRIPTION_CANCELLED: {
    title: 'Subscription cancelled',
    description:
      'Your subscription is cancelled. Please contact support to restore access.',
    severity: 'error'
  },
  SUBSCRIPTION_GRACE: {
    title: 'Subscription grace period',
    description:
      'Your subscription is in grace period. Please renew before access is blocked.',
    severity: 'warning'
  },
  SUBSCRIPTION_STATUS_INVALID: {
    title: 'Subscription status unavailable',
    description:
      'We could not load your subscription status. Please refresh and try again.',
    severity: 'warning'
  },
  // ---- Stage 5N-8: subscription renewal ----
  NO_ACTIVE_SUBSCRIPTION: {
    title: 'No subscription found',
    description:
      'We could not find an active subscription on this account. Please contact support.',
    severity: 'error',
    appendSupport: true
  },
  ORDER_NOT_FOUND: {
    title: 'Renewal order not found',
    description:
      'We could not match this payment to a renewal order. Please retry the renewal.',
    severity: 'error'
  },
  SIGNATURE_INVALID: {
    title: 'Payment verification failed',
    description:
      'We could not verify the payment with the gateway. Please contact support if the amount was charged.',
    severity: 'error',
    appendSupport: true
  },
  PAYMENT_NOT_VERIFIED: {
    title: 'Payment not verified',
    description:
      'The payment did not match the expected amount. Please contact support if you were charged.',
    severity: 'error',
    appendSupport: true
  },
  QUOTE_ZERO: {
    title: 'Cannot renew',
    description:
      'The computed renewal amount is zero. Please refresh the page or contact support.',
    severity: 'warning'
  },
  ORDER_CREATE_FAILED: {
    title: 'Order could not be created',
    description:
      'We could not create the payment order. Please try again in a moment.',
    severity: 'error',
    appendSupport: true
  },
  RAZORPAY_HTTP_ERROR: {
    title: 'Order could not be created',
    description:
      'The payment gateway rejected the order. Please try again in a moment.',
    severity: 'error',
    appendSupport: true
  },
  RAZORPAY_UPSTREAM_ERROR: {
    title: 'Payment service unreachable',
    description:
      'The payment gateway is temporarily unreachable. Please try again in a moment.',
    severity: 'error'
  },
  RAZORPAY_TIMEOUT: {
    title: 'Payment service timed out',
    description:
      'The payment gateway took too long to respond. Please try again.',
    severity: 'error'
  },
  RAZORPAY_CREDENTIALS_MISSING: {
    title: 'Payments are not configured',
    description:
      'Online payments are temporarily unavailable. Please contact support.',
    severity: 'error',
    appendSupport: true
  },
  ORDER_NOT_FOUND: {
    title: 'Order not found',
    description:
      'We could not find the order to verify. Please get a fresh quote and try again.',
    severity: 'warning'
  },
  SIGNATURE_INVALID: {
    title: 'Payment signature mismatch',
    description:
      'The payment signature could not be verified. If your amount was deducted, please contact support.',
    severity: 'modal',
    appendSupport: true
  },
  PAYMENT_NOT_VERIFIED: {
    title: 'Payment verification failed',
    description:
      'If your amount was deducted, please contact support. Do not pay again immediately.',
    severity: 'modal',
    appendSupport: true
  },
  SIGNUP_NOT_VERIFIED: {
    title: 'Verification required',
    description:
      'Please verify both mobile and email OTPs before continuing.',
    severity: 'warning'
  },
  PLAN_NOT_FOUND: {
    title: 'Plan unavailable',
    description:
      'The selected plan could not be loaded. Please refresh and try again.',
    severity: 'error'
  },
  PLAN_INACTIVE: {
    title: 'Plan unavailable',
    description:
      'This plan is no longer available. Please choose another plan.',
    severity: 'warning'
  },

  // ---- transport ----
  RATE_LIMITED: {
    title: 'Too many attempts',
    description: 'Please wait for a while before trying again.',
    severity: 'warning'
  },
  NETWORK_ERROR: {
    title: 'Unable to connect',
    description: 'Please check your internet connection and try again.',
    severity: 'error'
  },
  SERVER_ERROR: {
    title: 'Something went wrong',
    description:
      'We could not complete your request. Please try again.',
    severity: 'error',
    appendSupport: true
  },
  // Stage 5N-9A — invoice PDF download via Jasper Report Service.
  REPORT_SERVICE_UNAVAILABLE: {
    title: 'Report service unavailable',
    description:
      'PDF generation is temporarily unavailable. Please try again in a moment.',
    severity: 'warning',
    appendSupport: true
  },
  REPORT_SERVICE_TIMEOUT: {
    title: 'Report timed out',
    description:
      'The PDF took too long to generate. Please try again.',
    severity: 'warning'
  },
  REPORT_SERVICE_FORBIDDEN: {
    title: 'Report service rejected the request',
    description:
      'The report service refused our shared-secret token. An operator has been notified.',
    severity: 'error',
    appendSupport: true
  },
  REPORT_SERVICE_BAD_REQUEST: {
    title: 'Could not render invoice',
    description:
      'The report service rejected the invoice payload. Please contact support.',
    severity: 'error',
    appendSupport: true
  },
  REPORT_SERVICE_ERROR: {
    title: 'Report service error',
    description:
      'Report generation failed. Please try again or contact support.',
    severity: 'error',
    appendSupport: true
  },
  REPORT_RENDER_FAILED: {
    title: 'Could not render report',
    description:
      'The invoice report could not be rendered. Please try again or contact support.',
    severity: 'error',
    appendSupport: true
  },
  INVOICE_NOT_FOUND: {
    title: 'Invoice not found',
    description:
      'This invoice does not exist or no longer belongs to your account.',
    severity: 'warning'
  },

  // ---- Stage R1: reusable report engine ----
  REPORT_DEFINITION_NOT_FOUND: {
    title: 'Report not available',
    description:
      'The report you requested is not available. It may have been retired or your account may not have access.',
    severity: 'warning'
  },
  REPORT_FORMAT_NOT_ALLOWED: {
    title: 'Report format not supported',
    description:
      'This report does not support the requested format. Please choose another format.',
    severity: 'warning'
  },
  REPORT_PARAM_INVALID: {
    title: 'Report parameters invalid',
    description:
      'Some of the report parameters are missing or out of range. Please review your selections and try again.',
    severity: 'warning'
  },
  REPORT_ACCESS_DENIED: {
    title: 'Report access denied',
    description:
      'You do not have access to this report.',
    severity: 'warning'
  },
  REPORT_LIMIT_EXCEEDED: {
    title: 'Too many reports running',
    description:
      'There are too many reports currently queued or running for your account. Wait for one to finish before requesting another.',
    severity: 'warning'
  },
  REPORT_JOB_NOT_FOUND: {
    title: 'Report not found',
    description:
      'This report does not exist or no longer belongs to your account.',
    severity: 'warning'
  },
  REPORT_JOB_NOT_READY: {
    title: 'Report not ready yet',
    description:
      'The report is still queued or running. Please wait for it to finish before opening or emailing it.',
    severity: 'info'
  },
  REPORT_JOB_CANCELLED: {
    title: 'Report cancelled',
    description: 'The report request has been cancelled.',
    severity: 'info'
  },
  REPORT_EXPORT_NOT_SUPPORTED: {
    title: 'Export format not supported',
    description:
      'This export format is not available yet. Use HTML for now.',
    severity: 'warning'
  },
  REPORT_EMAIL_FAILED: {
    title: 'Could not email the report',
    description:
      'The report email could not be sent. Please try again or contact support.',
    severity: 'error',
    appendSupport: true
  },
  REPORT_STORAGE_FAILED: {
    title: 'Could not save the report',
    description:
      'The report could not be stored. Please try again or contact support.',
    severity: 'error',
    appendSupport: true
  },
  // Stage R2 — production hardening codes.
  REPORT_TOO_LARGE: {
    title: 'Report is too large',
    description:
      'This report exceeded the configured size limit. Narrow the date ' +
      'range or split the request and try again.',
    severity: 'warning'
  },
  REPORT_TIMEOUT: {
    title: 'Report timed out',
    description:
      'The report engine took too long to render this report. Try again with a ' +
      'narrower scope, or contact support if this keeps happening.',
    severity: 'error',
    appendSupport: true
  },
  REPORT_DEFINITION_INACTIVE: {
    title: 'Report is currently disabled',
    description:
      'An operator has disabled this report. Try again later or pick a different report.',
    severity: 'warning'
  }
};

const FALLBACK = {
  code: 'UNKNOWN',
  title: 'Something went wrong',
  description: 'Please try again. If the issue continues, contact support.',
  severity: 'error'
};

// Raw lower-cased message strings -> canonical CODE_MAP key. Used
// when the backend doesn't send an error.code (older endpoints,
// proxy errors) so we still translate familiar text into friendly
// copy instead of echoing the technical phrase.
const MESSAGE_MAP = {
  'invalid credentials': 'INVALID_CREDENTIALS',
  unauthorized: 'UNAUTHORIZED',
  forbidden: 'FORBIDDEN',
  'network error': 'NETWORK_ERROR',
  'timeout exceeded': 'NETWORK_ERROR',
  'request failed with status code 500': 'SERVER_ERROR',
  'request failed with status code 502': 'SERVER_ERROR',
  'request failed with status code 503': 'SERVER_ERROR',
  'request failed with status code 504': 'SERVER_ERROR'
};

function lookupByMessage(rawMessage) {
  if (typeof rawMessage !== 'string') return null;
  const key = rawMessage.trim().toLowerCase();
  if (!key) return null;
  const code = MESSAGE_MAP[key];
  return code ? { ...CODE_MAP[code], code } : null;
}

function classifyByStatus(err) {
  if (!err) return null;
  if (
    err.code === 'ECONNABORTED' ||
    err.message === 'Network Error' ||
    !err.response
  ) {
    return { ...CODE_MAP.NETWORK_ERROR, code: 'NETWORK_ERROR' };
  }
  const status = err.response.status;
  if (status === 401)
    return { ...CODE_MAP.UNAUTHORIZED, code: 'UNAUTHORIZED' };
  if (status === 403) return { ...CODE_MAP.FORBIDDEN, code: 'FORBIDDEN' };
  if (status === 429)
    return { ...CODE_MAP.RATE_LIMITED, code: 'RATE_LIMITED' };
  if (status >= 500)
    return { ...CODE_MAP.SERVER_ERROR, code: 'SERVER_ERROR' };
  return null;
}

function appendSupportLineIfNeeded(description, mapping) {
  if (!mapping || !mapping.appendSupport) return description;
  const supportLine =
    'Need help? Contact support at ' +
    supportConfig.email +
    ' or ' +
    supportConfig.mobile +
    '.';
  return description ? `${description} ${supportLine}` : supportLine;
}

// Public: turn an axios/api error into a safe { code, title,
// description, severity, requestId }. Caller controls the surface
// (toast vs modal) by reading severity, or just calls notifyError
// which dispatches automatically.
export function parseApiError(error, fallback = {}) {
  const baseFallback = {
    code: FALLBACK.code,
    title: fallback.title || FALLBACK.title,
    description: fallback.description || FALLBACK.description,
    severity: fallback.severity || FALLBACK.severity,
    requestId: undefined
  };
  if (!error) return baseFallback;

  const requestId =
    error.response && error.response.data && error.response.data.requestId;

  // 1. Plain Error with our own .code (thrown by frontend code, e.g.
  //    INVALID_EMAIL_DOMAIN from AuthContext).
  if (
    typeof error === 'object' &&
    error.code &&
    CODE_MAP[error.code] &&
    !error.response
  ) {
    return {
      code: error.code,
      title: CODE_MAP[error.code].title,
      description: appendSupportLineIfNeeded(
        CODE_MAP[error.code].description,
        CODE_MAP[error.code]
      ),
      severity: CODE_MAP[error.code].severity || 'error',
      requestId
    };
  }

  // 2. Backend envelope with a known code.
  const envelope =
    error.response && error.response.data && error.response.data.error;
  if (envelope && envelope.code && CODE_MAP[envelope.code]) {
    const m = CODE_MAP[envelope.code];
    return {
      code: envelope.code,
      title: m.title,
      description: appendSupportLineIfNeeded(m.description, m),
      severity: m.severity || 'error',
      requestId
    };
  }

  // 3. Backend message recognisable by raw text (no code).
  if (envelope && typeof envelope.message === 'string') {
    const byMessage = lookupByMessage(envelope.message);
    if (byMessage) {
      return {
        code: byMessage.code,
        title: byMessage.title,
        description: appendSupportLineIfNeeded(
          byMessage.description,
          byMessage
        ),
        severity: byMessage.severity || 'error',
        requestId
      };
    }
  }

  // 4. Transport-level fallback by HTTP status / network error.
  const classified = classifyByStatus(error);
  if (classified) {
    return {
      code: classified.code,
      title: classified.title,
      description: appendSupportLineIfNeeded(
        classified.description,
        classified
      ),
      severity: classified.severity || 'error',
      requestId
    };
  }

  // 5. Plain Error.message, but only when it's short and obviously
  //    not a stack trace / dump. Anything longer is replaced by the
  //    generic fallback.
  if (
    error.message &&
    typeof error.message === 'string' &&
    error.message.length < 160 &&
    !/^Request failed with status/i.test(error.message) &&
    !/\sat\s/.test(error.message)
  ) {
    const byMessage = lookupByMessage(error.message);
    if (byMessage) {
      return {
        code: byMessage.code,
        title: byMessage.title,
        description: appendSupportLineIfNeeded(
          byMessage.description,
          byMessage
        ),
        severity: byMessage.severity || 'error',
        requestId
      };
    }
    return {
      code: 'UNKNOWN',
      title: baseFallback.title,
      description: error.message,
      severity: baseFallback.severity,
      requestId
    };
  }

  return baseFallback;
}

// ---------------- low-level dispatchers ----------------------------

function devLog(prefix, err) {
  try {
    const env =
      (typeof import.meta !== 'undefined' && import.meta.env) || {};
    if (!env.DEV) return;
    if (typeof console === 'undefined' || !console.warn) return;
    const status = err && err.response && err.response.status;
    const code =
      err &&
      err.response &&
      err.response.data &&
      err.response.data.error &&
      err.response.data.error.code;
    console.warn(prefix, { status, code });
  } catch (_e) {
    /* noop */
  }
}

function noBridgeWarn(api) {
  try {
    const env =
      (typeof import.meta !== 'undefined' && import.meta.env) || {};
    if (env.DEV && typeof console !== 'undefined' && console.warn) {
      console.warn(
        '[notify] dropped ' + api + ' — NotifyBridge not yet registered'
      );
    }
  } catch (_e) {
    /* noop */
  }
}

// Build a popup-API surface that talks to the supplied AntD
// instances. Used by both the module-level singletons (via the
// bridge) and useAppNotifications() (via App.useApp()).
function buildApi({ notification, modal, message }) {
  function notifySuccess(title, description, options = {}) {
    if (!notification) return noBridgeWarn('notifySuccess');
    notification.success({
      message: title,
      description,
      placement: options.placement || 'topRight',
      duration: options.duration ?? 4
    });
  }

  function notifyInfo(title, description, options = {}) {
    if (!notification) return noBridgeWarn('notifyInfo');
    notification.info({
      message: title,
      description,
      placement: options.placement || 'topRight',
      duration: options.duration ?? 4
    });
  }

  function notifyWarning(title, description, options = {}) {
    // Default to a blocking modal.warning so important warnings don't
    // disappear before the user reads them. Callers can opt back into
    // a toast with { surface: 'toast' } when the message is genuinely
    // benign (e.g. background sync hiccup).
    const surface = options.surface || 'modal-warning';
    if (surface === 'toast' || surface === 'toast-warning') {
      if (!notification) return noBridgeWarn('notifyWarning(toast)');
      notification.warning({
        message: title,
        description,
        placement: options.placement || 'topRight',
        duration: options.duration ?? 6
      });
      return;
    }
    if (!modal) return noBridgeWarn('notifyWarning(modal)');
    modal.warning({
      title,
      content: description,
      okText: options.okText || 'OK',
      maskClosable: options.maskClosable !== false
    });
  }

  function notifyError(error, fallback = {}, options = {}) {
    devLog('[notifyError]', error);
    const parsed = parseApiError(error, fallback);
    // Resolve target surface from caller override -> CODE_MAP severity
    // -> 'error' default. The two key invariants:
    //   * 'error'/'modal'        -> modal.error      (blocking)
    //   * 'warning'/'modal-warn' -> modal.warning    (blocking)
    // Toast surfaces are opt-in via options.surface.
    const surface = options.surface || parsed.severity || 'error';

    if (surface === 'modal' || surface === 'error') {
      if (!modal) return noBridgeWarn('notifyError(modal)');
      modal.error({
        title: parsed.title,
        content: parsed.description,
        okText: options.okText || 'OK',
        maskClosable: options.maskClosable !== false
      });
      return;
    }
    if (surface === 'modal-warning' || surface === 'warning') {
      if (!modal) return noBridgeWarn('notifyError(modal-warning)');
      modal.warning({
        title: parsed.title,
        content: parsed.description,
        okText: options.okText || 'OK',
        maskClosable: options.maskClosable !== false
      });
      return;
    }
    if (surface === 'info' || surface === 'toast-info') {
      if (!notification) return noBridgeWarn('notifyError(toast-info)');
      notification.info({
        message: parsed.title,
        description: parsed.description,
        placement: options.placement || 'topRight',
        duration: options.duration ?? 4
      });
      return;
    }
    if (surface === 'toast-warning') {
      if (!notification) return noBridgeWarn('notifyError(toast-warning)');
      notification.warning({
        message: parsed.title,
        description: parsed.description,
        placement: options.placement || 'topRight',
        duration: options.duration ?? 6
      });
      return;
    }
    // surface === 'toast' or 'toast-error' — explicit opt-out from
    // the modal default. Used for non-actionable error feedback.
    if (!notification) return noBridgeWarn('notifyError(toast)');
    notification.error({
      message: parsed.title,
      description: parsed.description,
      placement: options.placement || 'topRight',
      duration: options.duration ?? 6
    });
  }

  function showErrorModal(error, fallback = {}, options = {}) {
    devLog('[showErrorModal]', error);
    if (!modal) return noBridgeWarn('showErrorModal');
    const parsed = parseApiError(error, fallback);
    modal.error({
      title: parsed.title,
      content: parsed.description,
      okText: options.okText || 'OK',
      maskClosable: options.maskClosable !== false
    });
  }

  function showWarningModal(title, description, options = {}) {
    if (!modal) return noBridgeWarn('showWarningModal');
    modal.warning({
      title,
      content: description,
      okText: options.okText || 'OK',
      maskClosable: options.maskClosable !== false
    });
  }

  function showSuccessModal(title, description, options = {}) {
    if (!modal) return noBridgeWarn('showSuccessModal');
    modal.success({
      title,
      content: description,
      okText: options.okText || 'OK',
      maskClosable: options.maskClosable !== false
    });
  }

  function flashMessage(text, kind = 'info') {
    if (!message) return noBridgeWarn('flashMessage');
    if (kind === 'success') message.success(text);
    else if (kind === 'error') message.error(text);
    else if (kind === 'warning') message.warning(text);
    else message.info(text);
  }

  return {
    notifySuccess,
    notifyInfo,
    notifyWarning,
    notifyError,
    showErrorModal,
    showWarningModal,
    showSuccessModal,
    flashMessage
  };
}

// ---------------- module-level public exports ----------------------

// These read the bridge-registered instances at call time. Use them
// from non-React code (axios interceptors, plain modules) where the
// React tree is unavailable.
export function notifySuccess(...args) {
  buildApi({ notification: _notification, modal: _modal, message: _message })
    .notifySuccess(...args);
}
export function notifyInfo(...args) {
  buildApi({ notification: _notification, modal: _modal, message: _message })
    .notifyInfo(...args);
}
export function notifyWarning(...args) {
  buildApi({ notification: _notification, modal: _modal, message: _message })
    .notifyWarning(...args);
}
export function notifyError(...args) {
  buildApi({ notification: _notification, modal: _modal, message: _message })
    .notifyError(...args);
}
export function showErrorModal(...args) {
  buildApi({ notification: _notification, modal: _modal, message: _message })
    .showErrorModal(...args);
}
export function showWarningModal(...args) {
  buildApi({ notification: _notification, modal: _modal, message: _message })
    .showWarningModal(...args);
}
export function showSuccessModal(...args) {
  buildApi({ notification: _notification, modal: _modal, message: _message })
    .showSuccessModal(...args);
}
export function flashMessage(...args) {
  buildApi({ notification: _notification, modal: _modal, message: _message })
    .flashMessage(...args);
}

// Backward-compat re-export. extractFriendly used to return only
// {title, description}; parseApiError is the superset. Older
// imports keep working.
export function extractFriendly(error, fallback = {}) {
  const parsed = parseApiError(error, fallback);
  return { title: parsed.title, description: parsed.description };
}

// ---------------- React hook ---------------------------------------

// Preferred entry-point inside components. The methods returned here
// are bound to the live AntApp.useApp() instance for THIS render —
// so they always pick up theme + ConfigProvider context, even when
// the bridge hasn't registered yet (e.g. during a synchronous error
// fired in the same render that mounted <NotifyBridge>).
export function useAppNotifications() {
  const { notification, modal, message } = AntApp.useApp();
  return useMemo(
    () => buildApi({ notification, modal, message }),
    [notification, modal, message]
  );
}
